import { Dispatch, SetStateAction, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from "@mui/material";

import { fetchAuthForgotSubmit, fetchAuthForgotWith2FA, SetSnackType } from "../../utils";
import { Password, validatePassword } from ".";

export const DialogForgotPw = (props: {
  open: boolean;
  reRegistered: (regUn: string, regPw: string) => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setSnack: SetSnackType;
}) => {
  const [un, setUn] = useState("");
  const [unErr, setUnErr] = useState(false);

  const [open2FA, setOpen2FA] = useState(false);
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState(false);
  const [pw, setPw] = useState("");
  const [pwErr, setPwErr] = useState(false);

  const handleForgotSubmit = async () => {
    if (!un) {
      setUnErr(true);
      props.setSnack("Invalid username", "error");
      return;
    }

    try {
      const res = await fetchAuthForgotSubmit(un ?? "");
      if (res.error) {
        setUnErr(true);
        props.setSnack(res.error === "No user found" ? `Username "${un}" not found` : res.error, "error");
        return;
      }
    } catch (err: any) {
      props.setSnack(err.toString, "error");
      return;
    }
    setOpen2FA(true);
  };

  const handleForgotWith2FA = async () => {
    if (!code) {
      setCodeErr(true);
      props.setSnack("Check your email for the 2FA code", "error");
      return;
    }
    const { fail, err } = validatePassword(pw);
    if (fail) return props.setSnack(err, "error");

    try {
      const res = await fetchAuthForgotWith2FA(un, code, pw);
      if (res.error) {
        if (res.error === "No user found") {
          setUnErr(true);
          props.setSnack(`Username "${un}" not found`, "error");
        } else {
          setCodeErr(true);
          props.setSnack(res.error, "error");
        }
        return;
      }
    } catch (err: any) {
      props.setSnack(err.toString, "error");
      return;
    }
    props.reRegistered(un, pw);
    setUn("");
    setPw("");
    setCode("");
    setOpen2FA(false);
    props.setOpen(false);
  };

  return (
    <>
      <Dialog open={props.open} disableRestoreFocus>
        <DialogTitle>Forgot Password?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter your username here to receive an email code to set a new password:
          </DialogContentText>
          <TextField
            autoComplete="off"
            autoFocus
            error={unErr}
            fullWidth
            label="Username"
            required
            type="text"
            onChange={ev => {
              setUnErr(false);
              setUn(ev.target.value ?? "");
            }}
            onKeyDownCapture={ev => {
              if (ev.key === "Enter") handleForgotSubmit();
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUn("");
              props.setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button disabled={!un} onClick={handleForgotSubmit} variant="outlined">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={open2FA} disableRestoreFocus>
        <DialogTitle>Confirm Your New Password: 2FA</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Check your email for the two-factor authentication code, and enter it to confirm ownership of your existing
            account, then set your new password:
          </DialogContentText>
          <form>
            <TextField
              autoComplete="off"
              disabled
              fullWidth
              label="Your Username"
              type="text"
              sx={{ mt: 2 }}
              value={un}
            />
            <TextField
              autoFocus
              error={codeErr}
              fullWidth
              label="2FA Code"
              required
              type="text"
              onChange={ev => {
                setCodeErr(false);
                setCode(ev.target.value ?? "");
              }}
              onKeyUpCapture={ev => {
                if (ev.key === "Enter") handleForgotWith2FA();
              }}
              sx={{ mt: 2 }}
              value={code}
            />
            <Password
              withReqs
              {...{ pw, pwErr, setPw, setPwErr, setSnack: props.setSnack, submit: handleForgotSubmit }}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCode("");
              setPw("");
              setOpen2FA(false);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button disabled={!code || !pw} onClick={handleForgotWith2FA} variant="contained">
            Confirm New Password
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
